<script setup lang="ts">
  import {
    Icon,
    IconName,
    IconSize,
    Pill,
    PillAppearance,
    PillColor,
    Stars,
    Swiper,
    Tag,
    TagAppearance,
    TagColor,
    TagSize,
    Typo,
    TypoVariant,
  } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import {
    ConfirmationType,
    ConfirmationTypeIcon,
    ConfirmationTypeSheet,
    CONFIRMATION_TYPE_TITLES,
    getProductsConfirmationType,
  } from '@/entities/confirmationType';
  import { type PrepaymentOperator, PrepaymentSheet } from '@/entities/prepayment';
  import type { ProductType } from '@/shared/config/pages';
  import type { Country } from '@/shared/model';
  import RatingBadge from '@/shared/ui/RatingBadge.vue';
  import SerpFeaturesSheet from './SerpFeaturesSheet.vue';
  import { SERP_CARD_VISIBLE_FEATURES } from '../config/card';
  import { SERP_CARD_TEXTS } from '../config/texts';
  import { getSerpCardFeatures, type SerpCardFeature } from '../lib/serpCardFeatures';
  import {
    getSerpCardBooking,
    getSerpCardCountry,
    getSerpCardLocation,
    getSerpCardOrders,
    getSerpCardPhotos,
    getSerpCardPrepayment,
    getSerpCardPrepaymentOperators,
    getSerpCardPrice,
    getSerpCardRenovation,
    getSerpCardReviews,
    getSerpCardStars,
    isSerpCardFewPlaces,
    type SerpCardReviews,
  } from '../lib/serpCardView';
  import type { SerpHotelOpenOptions, SerpProduct } from '../model/types';

  interface SerpCardProps {
    product: SerpProduct;
    isVisiblePrepayment?: boolean;
  }

  const props = withDefaults(defineProps<SerpCardProps>(), {
    isVisiblePrepayment: true,
  });

  const emit = defineEmits<{ (e: 'open', options: SerpHotelOpenOptions): void }>();

  const photoIndex = ref(0);
  const isFeaturesSheetOpen = ref(false);
  const isConfirmationSheetOpen = ref(false);
  const isPrepaymentSheetOpen = ref(false);

  const photos = computed<string[]>(() => getSerpCardPhotos(props.product));
  const stars = computed<number>(() => getSerpCardStars(props.product));
  const renovation = computed(() => getSerpCardRenovation(props.product));
  const location = computed<string>(() => getSerpCardLocation(props.product));
  const price = computed<string>(() => getSerpCardPrice(props.product));
  const booking = computed<string>(() => getSerpCardBooking(props.product));
  const prepayment = computed<string | null>(() => getSerpCardPrepayment(props.product));
  const prepaymentOperators = computed<PrepaymentOperator[]>(() => getSerpCardPrepaymentOperators(props.product));
  const country = computed<Country | null>(() => getSerpCardCountry(props.product));
  const productType = computed<ProductType | null>(() => props.product.products[0]?.type ?? null);
  const reviews = computed<SerpCardReviews>(() => getSerpCardReviews(props.product));
  const orders = computed<string>(() => getSerpCardOrders(props.product));
  const isFewPlaces = computed<boolean>(() => isSerpCardFewPlaces(props.product));
  const confirmation = computed<ConfirmationType | null>(() => getProductsConfirmationType(props.product.products));
  const visibleConfirmation = computed<ConfirmationType | null>(() =>
    confirmation.value === ConfirmationType.Quick ? null : confirmation.value,
  );

  const features = computed<SerpCardFeature[]>(() => getSerpCardFeatures(props.product));
  const visibleFeatures = computed<SerpCardFeature[]>(() => features.value.slice(0, SERP_CARD_VISIBLE_FEATURES));
  const hasHiddenFeatures = computed<boolean>(() => features.value.length > SERP_CARD_VISIBLE_FEATURES);
  const hotelCategoryName = computed(() => props.product.products[0].hotelCategory?.name || '');

  const swiperHeight = computed(() => (photoIndex.value === 0 ? 180 : 272));

  const openFeaturesSheet = (): void => {
    isFeaturesSheetOpen.value = true;
  };

  const closeFeaturesSheet = (): void => {
    isFeaturesSheetOpen.value = false;
  };

  const openConfirmationSheet = (): void => {
    isConfirmationSheetOpen.value = true;
  };

  const closeConfirmationSheet = (): void => {
    isConfirmationSheetOpen.value = false;
  };

  const openPrepaymentSheet = (): void => {
    isPrepaymentSheetOpen.value = true;
  };

  const closePrepaymentSheet = (): void => {
    isPrepaymentSheetOpen.value = false;
  };
</script>

<template>
  <article
    class="serp-card"
    data-testid="serp-card"
    @click="emit('open', { toReviews: false })"
  >
    <div
      class="serp-card__media"
      data-testid="serp-card-media"
      :style="`height: ${swiperHeight}px`"
    >
      <Swiper
        v-if="photos.length"
        v-model="photoIndex"
        class="serp-card__swiper"
        :images="photos"
      />
      <div
        v-else
        class="serp-card__media-placeholder"
        role="img"
        :aria-label="SERP_CARD_TEXTS.photoAlt"
        data-testid="serp-card-media-placeholder"
      />
      <Pill
        v-if="isFewPlaces"
        class="serp-card__few-places"
        :color="PillColor.Red"
        :appearance="PillAppearance.Primary"
        data-testid="serp-card-few-places"
      >
        {{ SERP_CARD_TEXTS.fewPlaces }}
      </Pill>
    </div>

    <div class="serp-card__info">
      <div class="serp-card__hotel">
        <div class="serp-card__summary">
          <div class="serp-card__rating">
            <Stars
              v-if="stars"
              class="serp-card__stars"
              :model-value="stars"
              :stars="stars"
              size="s"
              data-testid="serp-card-stars"
            />
            <Typo
              v-else-if="hotelCategoryName"
              class="serp-card__hotel-category-name"
            >
              {{ hotelCategoryName }}
            </Typo>

            <div
              v-if="renovation"
              class="serp-card__renovation"
              data-testid="serp-card-renovation"
            >
              <Typo
                class="serp-card__renovation-year"
                :variant="TypoVariant.BodyMRegular"
                tag="span"
                data-testid="serp-card-renovation-year"
              >
                {{ `${renovation.year} ${SERP_CARD_TEXTS.yearSuffix}` }}
              </Typo>
              <Typo
                class="serp-card__renovation-label"
                :variant="TypoVariant.BodyMRegular"
                tag="span"
                data-testid="serp-card-renovation-label"
              >
                {{ renovation.label }}
              </Typo>
            </div>
          </div>

          <div class="serp-card__titles">
            <Typo
              class="serp-card__name"
              :variant="TypoVariant.Heading6"
              tag="p"
              data-testid="serp-card-name"
            >
              {{ product.hotel.name }}
            </Typo>
            <Typo
              v-if="location"
              class="serp-card__location"
              :variant="TypoVariant.BodyMRegular"
              tag="span"
              data-testid="serp-card-location"
            >
              {{ location }}
            </Typo>
          </div>

          <div class="serp-card__meta">
            <div
              class="serp-card__reviews"
              data-testid="serp-card-reviews"
              @click.stop="emit('open', { toReviews: reviews.hasReviews })"
            >
              <RatingBadge
                :rating="reviews.rating"
                testid="serp-card-rating"
              />

              <div class="serp-card__reviews-count">
                <Typo
                  :variant="TypoVariant.BodyMRegular"
                  tag="span"
                  data-testid="serp-card-reviews-count"
                >
                  {{ reviews.countLabel }}
                </Typo>
                <span
                  v-if="reviews.hasReviews"
                  class="serp-card__reviews-underline"
                  data-testid="serp-card-reviews-underline"
                />
              </div>
            </div>

            <template v-if="orders">
              <Icon
                class="serp-card__meta-separator"
                :name="IconName.PointFilled"
                :size="IconSize.XXS"
              />
              <Typo
                class="serp-card__orders"
                :variant="TypoVariant.BodyMRegular"
                tag="span"
                data-testid="serp-card-orders"
              >
                {{ orders }}
              </Typo>
            </template>

            <template v-if="visibleConfirmation">
              <Icon
                class="serp-card__meta-separator"
                :name="IconName.PointFilled"
                :size="IconSize.XXS"
              />
              <button
                class="serp-card__confirmation"
                type="button"
                :aria-label="CONFIRMATION_TYPE_TITLES[visibleConfirmation]"
                data-testid="serp-card-confirmation"
                @click.stop="openConfirmationSheet"
              >
                <ConfirmationTypeIcon
                  :type="visibleConfirmation"
                  :size="IconSize.M"
                />
              </button>
            </template>
          </div>
        </div>

        <div
          v-if="features.length"
          class="serp-card__features"
          data-testid="serp-card-features"
        >
          <Tag
            v-for="feature in visibleFeatures"
            :key="feature.id"
            :color="TagColor.Black"
            :appearance="TagAppearance.Tertiary"
            :size="TagSize.M"
            :data-testid="`serp-card-feature-${feature.id}`"
          >
            <Typo
              :variant="TypoVariant.BodyMRegular"
              tag="span"
            >
              {{ feature.label }}
            </Typo>
          </Tag>

          <button
            v-if="hasHiddenFeatures"
            class="serp-card__features-more"
            type="button"
            :aria-label="SERP_CARD_TEXTS.allFeatures"
            data-testid="serp-card-features-more"
            @click.stop="openFeaturesSheet"
          >
            <Tag
              :color="TagColor.Black"
              :appearance="TagAppearance.Tertiary"
              :size="TagSize.M"
            >
              <Icon
                class="serp-card__feature-more-icon"
                name="more"
              />
            </Tag>
          </button>
        </div>
      </div>

      <!-- Бронь, предоплата и цена — один блок: в макете они идут вплотную, шагом 2px. -->
      <div
        v-if="booking || prepayment || price"
        class="serp-card__price-block"
      >
        <Typo
          v-if="booking"
          class="serp-card__booking"
          :variant="TypoVariant.BodyMRegular"
          tag="span"
          data-testid="serp-card-booking"
        >
          {{ booking }}
        </Typo>

        <button
          v-if="prepayment && isVisiblePrepayment"
          class="serp-card__prepayment"
          type="button"
          data-testid="serp-card-prepayment"
          @click.stop="openPrepaymentSheet"
        >
          <Typo
            class="serp-card__prepayment-label"
            :variant="TypoVariant.BodyMRegular"
            tag="span"
            data-testid="serp-card-prepayment-label"
          >
            {{ prepayment }}
          </Typo>
          <Icon
            class="serp-card__prepayment-icon"
            :name="IconName.Question"
            :size="IconSize.M"
          />
        </button>

        <Typo
          v-if="price"
          class="serp-card__price"
          :variant="TypoVariant.Heading4"
          tag="span"
          data-testid="serp-card-price"
        >
          {{ price }}
        </Typo>
      </div>
    </div>

    <SerpFeaturesSheet
      v-if="isFeaturesSheetOpen"
      :features="features"
      @close="closeFeaturesSheet"
    />

    <ConfirmationTypeSheet
      v-if="visibleConfirmation && isConfirmationSheetOpen"
      :type="visibleConfirmation"
      @close="closeConfirmationSheet"
    />

    <PrepaymentSheet
      v-if="isPrepaymentSheetOpen"
      :operators="prepaymentOperators"
      :country="country"
      :product-type="productType"
      @close="closePrepaymentSheet"
    />
  </article>
</template>

<style scoped lang="scss">
  .serp-card {
    background: var(--color-bg-primary);
    border-radius: var(--rounding-xl);
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
    padding: var(--spacing-s);
    -webkit-tap-highlight-color: transparent;
    width: 100%;

    &__feature-more-icon {
      transform: rotate(-90deg);
    }

    &__media {
      border-radius: var(--rounding-m);
      height: 180px;
      overflow: hidden;
      position: relative;
      transition: height 0.44s cubic-bezier(0.25, 0.8, 0.25, 1);
      width: 100%;
    }

    &__swiper {
      height: 100%;
      width: 100%;
    }

    &__few-places {
      left: var(--spacing-xs);
      pointer-events: none;
      position: absolute;
      top: var(--spacing-xs);
      z-index: 1;
    }

    &__media-placeholder {
      background: var(--color-neutral-20);
      height: 100%;
      width: 100%;
    }

    &__info {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
    }

    &__hotel {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
    }

    &__summary {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    &__rating {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: var(--spacing-xs);
    }

    &__renovation {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: var(--spacing-xxs);
    }

    &__renovation-year {
      align-items: center;
      background: var(--color-neutral-10);
      border-radius: var(--rounding-xs);
      box-sizing: border-box;
      color: var(--color-text-primary);
      display: flex;
      min-height: 22px;
      padding: var(--spacing-xxxs) var(--spacing-xxs);
    }

    &__renovation-label {
      color: var(--color-text-secondary);
    }

    &__titles {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }

    &__name {
      color: var(--color-text-primary);

      // Длинные названия отелей переносим, а не режем: в макете название многострочное.
      overflow-wrap: anywhere;
    }

    &__location {
      color: var(--color-text-secondary);
    }

    // Строка растёт от «оценка + отзывы» до четырёх блоков с точками-разделителями,
    // поэтому переносится: на узком экране длинные числа иначе выдавили бы иконку за карточку.
    &__meta {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-xxxs);
    }

    &__meta-separator {
      color: var(--color-text-secondary);
      fill: var(--color-text-secondary);
      flex-shrink: 0;
    }

    &__orders {
      color: var(--color-text-secondary);
    }

    &__confirmation {
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      padding: 0;
    }

    &__reviews {
      align-items: baseline;
      display: flex;
      flex-direction: row;
      gap: var(--spacing-xxs);
    }

    &__reviews-count {
      color: var(--color-text-secondary);
      display: flex;
      flex-direction: column;
    }

    &__hotel-category-name {
      color: var(--color-warning-900);
    }

    &__reviews-underline {
      background-image: radial-gradient(circle, var(--color-text-secondary) 1px, transparent 1px);
      background-repeat: repeat-x;
      background-size: 4.5px 2px;
      height: 2px;
      width: 100%;
    }

    &__features {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-xs);
    }

    &__features-more {
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      padding: 0;
    }

    &__price-block {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
    }

    &__booking {
      color: var(--color-text-secondary);
    }

    // Цвет стоит на кнопке, а не на подписи: иконка-вопрос рисуется через currentColor,
    // поэтому зелёной становится заодно с текстом.
    &__prepayment {
      align-items: center;
      align-self: flex-start;
      background: none;
      border: none;
      color: var(--color-green-50);
      cursor: pointer;
      display: flex;
      fill: var(--color-green-50);
      gap: var(--spacing-xxs);
      padding: 0;
      text-align: left;
    }

    &__prepayment-icon {
      flex-shrink: 0;
    }

    &__price {
      color: var(--color-text-primary);
    }
  }
</style>
